import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './MobileNav.css';
import { pauseScroll, resumeScroll } from '../smoothScroll';

// The same five links the header shows on desktop.
const NAV_ITEMS = [
  { to: '/accounts', label: 'Accounts' },
  { to: '/scallop-pay', label: 'Scallop Pay' },
  { to: '/cards', label: 'Cards' },
  { to: '/scallop-plus', label: 'Scallop+' },
  { to: '/helpcenter', label: 'FAQs' },
];

// Framer's hamburger. Every exported page ships one of these two names —
// the current design calls it "Mobile Menu", the older /old-home layout
// calls it "Navbar Toggle" — and opening it was the job of the Framer
// runtime, which is stripped from this export. Matching on the exported
// attribute rather than on generated class names (or on markup patched
// into the page components) means this keeps working after
// scripts/convert.js regenerates every page from the Framer HTML.
const TOGGLE_SELECTOR =
  '[data-framer-name="Mobile Menu"],[data-framer-name="Navbar Toggle"]';

// Framer's phone breakpoint. Above it the real header nav is visible and
// this component must stay out of the way.
const PHONE_BREAKPOINT = '(max-width: 809.98px)';

function MobileNav() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const close = useCallback(() => setOpen(false), []);

  // Delegated so it binds once, regardless of which page is mounted or how
  // many breakpoint variants of the hamburger that page renders (only the
  // one matching the current breakpoint is ever clickable — the rest are
  // display:none).
  useEffect(() => {
    function onClick(event) {
      const toggle = event.target.closest?.(TOGGLE_SELECTOR);
      if (!toggle) return;
      if (!window.matchMedia(PHONE_BREAKPOINT).matches) return;
      event.preventDefault();
      event.stopPropagation();
      setOpen((wasOpen) => !wasOpen);
    }
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, []);

  // A resize past the breakpoint would otherwise leave the panel stuck open
  // behind the desktop header.
  useEffect(() => {
    const query = window.matchMedia(PHONE_BREAKPOINT);
    const onChange = (event) => {
      if (!event.matches) setOpen(false);
    };
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    // Without this the page behind the panel scrolls under the user's
    // finger instead of the panel.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    pauseScroll();
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      resumeScroll();
    };
  }, [open]);

  return (
    <div
      className={`mobile-nav ${open ? 'mobile-nav--open' : ''}`}
      aria-hidden={!open}
    >
      <button
        type="button"
        className="mobile-nav__backdrop"
        aria-label="Close menu"
        tabIndex={open ? 0 : -1}
        onClick={close}
      />
      <nav className="mobile-nav__panel" aria-label="Main">
        <div className="mobile-nav__header">
          <Link to="/" className="mobile-nav__brand" tabIndex={open ? 0 : -1}>
            Scallop
          </Link>
          <button
            type="button"
            className="mobile-nav__close"
            aria-label="Close menu"
            tabIndex={open ? 0 : -1}
            onClick={close}
          >
            <span />
            <span />
          </button>
        </div>
        <ul className="mobile-nav__list">
          {NAV_ITEMS.map((item) => (
            <li key={item.to}>
              <Link
                to={item.to}
                tabIndex={open ? 0 : -1}
                aria-current={
                  location.pathname === item.to ? 'page' : undefined
                }
              >
                {item.label}
              </Link>
            </li>
          ))}
        </ul>
        {/* Direct store link (OneLink smart URL routes to the App Store /
            Play Store per device). The ?olbm=1 suffix is deliberate: App's
            global click-interceptor exact-matches the bare onelink URL into
            the QR modal, so the suffixed URL sails past it into a real
            navigation instead. */}
        <a
          className="mobile-nav__cta"
          href="https://onelink.to/wch597?olbm=1"
          target="_blank"
          rel="noopener"
          tabIndex={open ? 0 : -1}
          onClick={close}
        >
          Get Started
          <span>Coming soon</span>
        </a>
      </nav>
    </div>
  );
}

export default MobileNav;
